import type { StoreApi } from "zustand";

import { withPipelineConfigSync } from "../../../../common/stores/pipelineConfigSyncStore";
import {
  createAccountAnalysisConfiguration,
  fetchAccountAnalysisConfiguration,
  updateAccountAnalysisConfiguration
} from "../../../../data/queries/accountAnalysis";
import { AccountAnalysisConfiguration } from "../../types";
import type { AccountAnalysisStore } from "../accountAnalysisStore";

export interface ConfigurationActions {
  // Sync actions
  setConfiguration: (config: AccountAnalysisConfiguration | null) => void;
  setIsLoadingConfiguration: (loading: boolean) => void;
  setConfigurationError: (error: string | null) => void;

  // Async actions
  fetchConfiguration: () => Promise<void>;
  createConfiguration: (config: Omit<AccountAnalysisConfiguration, "id" | "created" | "modified">) => Promise<void>;
  updateConfiguration: (updates: Partial<AccountAnalysisConfiguration>) => Promise<void>;
}

export const createConfigurationSlice = (
  set: StoreApi<AccountAnalysisStore>["setState"],
  get: StoreApi<AccountAnalysisStore>["getState"]
): ConfigurationActions => {
  const synced = withPipelineConfigSync(get);

  return {
  // Sync actions
    setConfiguration: (configuration) => set({ configuration }),
    setIsLoadingConfiguration: (isLoadingConfiguration) => set({ isLoadingConfiguration }),
    setConfigurationError: (configurationError) => set({ configurationError }),

    // Async actions
    fetchConfiguration: async () => {
      set({ isLoadingConfiguration: true, configurationError: null });
      try {
        const configuration = await fetchAccountAnalysisConfiguration();
        set({ configuration, isLoadingConfiguration: false });
      } catch (error) {
        set({
          configurationError: error instanceof Error ? error.message : "Failed to fetch configuration",
          isLoadingConfiguration: false
        });
      }
    },

    createConfiguration: async (configData) => {
      try {
        const configuration = await createAccountAnalysisConfiguration(configData);
        set({ configuration });
      } catch (error) {
        set({
          configurationError: error instanceof Error ? error.message : "Failed to create configuration"
        });
      }
    },

    updateConfiguration: synced(async (updates) => {
      const state = get();
      if (!state.configuration) {
        set({
          configurationError: "No configuration found to update"
        });
        return;
      }

      try {
        const updatedConfiguration = await updateAccountAnalysisConfiguration(
          state.configuration.id,
          updates
        );
        set({ configuration: updatedConfiguration });
      } catch (error) {
        set({
          configurationError: error instanceof Error ? error.message : "Failed to update configuration"
        });
        throw error;
      }
    })
  };
};
